import { io } from "socket.io-client";
import { ANIMAL_SPLIT_CORE } from "../constants";

const SOCKET_URL = ANIMAL_SPLIT_CORE[process.env.NODE_ENV].URL;

/**
 * Connect to the realtime gateway for a specific room.
 * Optional seatIndex will register seat presence (single active client per seat).
 * Returns a cleanup function that leaves the room/seat and closes the socket.
 */
export const connectRoomSocket = (roomId, onRoomUpdate, options = {}) => {
  if (!roomId) return () => undefined;

  const { seatIndex, onPresence, onSeatOccupied } = options;

  const socket = io(SOCKET_URL, {
    transports: ["websocket"],
    reconnectionAttempts: 5,
    reconnectionDelay: 500,
  });

  const handleRoomUpdate = (payload) => {
    if (payload?.id === roomId) {
      onRoomUpdate(payload);
    }
  };

  const handlePresence = (payload) => {
    if (payload?.roomId === roomId && Array.isArray(payload.connectedSeatIndices)) {
      onPresence?.(payload);
    }
  };

  const handleSeatOccupied = (payload) => {
    if (
      payload?.roomId === roomId &&
      payload?.seatIndex === seatIndex
    ) {
      onSeatOccupied?.(payload);
    }
  };

  socket.on("connect", () => {
    socket.emit("join-room", { roomId });
    if (seatIndex !== undefined && seatIndex !== null) {
      socket.emit("join-seat", { roomId, seatIndex });
    }
  });

  socket.on("room:update", handleRoomUpdate);
  socket.on("seat:presence", handlePresence);
  socket.on("seat:occupied", handleSeatOccupied);

  socket.on("connect_error", (err) => {
    console.error("Socket connection error", err);
  });

  return () => {
    if (seatIndex !== undefined && seatIndex !== null) {
      socket.emit("leave-seat", { roomId, seatIndex });
    }
    socket.emit("leave-room", { roomId });
    socket.off("room:update", handleRoomUpdate);
    socket.off("seat:presence", handlePresence);
    socket.off("seat:occupied", handleSeatOccupied);
    socket.disconnect();
  };
};

/**
 * Connect to the realtime gateway for a specific group (event) and subscribe to updates.
 * Returns a cleanup function that leaves the group and closes the socket.
 */
export const connectGroupSocket = (groupId, onGroupUpdate) => {
  if (!groupId) return () => undefined;

  const socket = io(SOCKET_URL, {
    transports: ["websocket"],
    reconnectionAttempts: 5,
    reconnectionDelay: 500,
  });

  const handleGroupUpdate = (payload) => {
    if (payload?.id === groupId) {
      onGroupUpdate?.(payload);
    }
  };

  socket.on("connect", () => {
    socket.emit("join-group", { groupId });
  });

  socket.on("group:update", handleGroupUpdate);

  socket.on("connect_error", (err) => {
    console.error("Socket connection error", err);
  });

  return () => {
    socket.emit("leave-group", { groupId });
    socket.off("group:update", handleGroupUpdate);
    socket.disconnect();
  };
};
